import FormGroup from 'components/FormGroup';
import Typography from 'components/Typography/Typography';
import styles from './ApiKeySettings.module.scss';
import Button from 'components/Button';
import {useApiKeySettings} from './useApiKeySettings';
import PlusIcon from 'pages/RouteHistory/icons/plus';
import DataTable from 'components/DataTable';
import CreateKeyModal from './components/CreateKeyModal';
import DeleteModal from 'components/DeleteModal';
import {useCallback, useMemo} from 'react';
import {APIKey} from 'types/api-key';
import {format} from 'date-fns';
import {Toggle} from './components/Toggle';
import toast from 'react-hot-toast';
import CopyIcon from '../../icons/copy';
import {API_DOCS_URL} from 'constants/url';

const transformData = (
  data: APIKey[],
  renderDeleteButton: (id: number) => React.ReactNode,
  renderCopyableKey: (key: string) => React.ReactNode,
  handleToggleActive: (id: number, checked: boolean) => Promise<void>
) => {
  return data.map((item) => ({
    ...item,
    created_at: format(new Date(item.created_at), 'MMM d, yyyy hh:mm aa'),
    delete: renderDeleteButton(item.id),
    key: renderCopyableKey(item.key),
    is_active: (
      <Toggle
        active={item.is_active}
        id={item.id}
        onChangeStatus={handleToggleActive}
      />
    ),
  }));
};

function ApiKeySettings() {
  const {
    API_KEY_LIST_SCHEMA,
    isLoadingApiKeys,
    isCreateKeyModalOpen,
    setIsCreateKeyModalOpen,
    createApiKeyMutation,
    isLoadingCreateKey,
    isDeleteKeyModalOpen,
    setIsDeleteKeyModalOpen,
    data,
    deleteApiKeyMutation,
    isDeleting,
    updateKeyMutation,
    deleteKeyId,
    handleDeleteKey,
  } = useApiKeySettings();

  const renderDeleteButton = useCallback(
    (id: number) => {
      return (
        <Button
          color="danger"
          className={styles.deleteButton}
          onClick={() => {
            handleDeleteKey(id);
          }}
          disabled={isDeleting}
        >
          Delete
        </Button>
      );
    },
    [handleDeleteKey, isDeleting]
  );

  const handleCopyKey = useCallback((key: string) => {
    navigator.clipboard.writeText(key);
    toast.success('API Key copied to clipboard');
  }, []);

  const renderCopyableKey = useCallback(
    (key: string) => {
      const trimmedKey = key.slice(0, 10) + '...' + key.slice(-4);
      return (
        <div className={styles.apiKeyItemKey}>
          <Typography variant="label" style={{margin: 0}}>
            {trimmedKey}
          </Typography>
          <CopyIcon
            width={14}
            height={14}
            className={styles.copyIcon}
            onClick={() => handleCopyKey(key)}
          />
        </div>
      );
    },
    [handleCopyKey]
  );
  const handleToggleActive = useCallback(
    async (id: number, checked: boolean) => {
      await updateKeyMutation({id, is_active: checked});
    },
    [updateKeyMutation]
  );

  const transformedData = useMemo(() => {
    return transformData(
      data || [],
      renderDeleteButton,
      renderCopyableKey,
      handleToggleActive
    );
  }, [data, renderDeleteButton, renderCopyableKey, handleToggleActive]);

  return (
    <FormGroup columns={1} className={styles.list}>
      <div className={styles.apiKeyHeader}>
        <Typography variant="label">
          API keys you have generated that can be used to access and manage your
          company's data via the&nbsp;
          <a
            href={API_DOCS_URL || ''}
            rel="noopener noreferrer"
            target="_blank"
            className={styles.learnMoreLink}
          >
            Magic Dispatch API.
          </a>
        </Typography>
        <Button color="primary" onClick={() => setIsCreateKeyModalOpen(true)}>
          <PlusIcon width={16} height={16} /> Create API Key
        </Button>
      </div>
      <DataTable
        schema={API_KEY_LIST_SCHEMA}
        data={transformedData}
        loading={isLoadingApiKeys}
        selectable={false}
      />
      {isCreateKeyModalOpen && (
        <CreateKeyModal
          isOpen={isCreateKeyModalOpen}
          onClose={() => setIsCreateKeyModalOpen(false)}
          onSubmit={(data) =>
            createApiKeyMutation({name: data.name, is_active: true})
          }
          loading={isLoadingCreateKey}
        />
      )}
      {isDeleteKeyModalOpen && (
        <DeleteModal
          openModal={isDeleteKeyModalOpen}
          setOpenModal={setIsDeleteKeyModalOpen}
          onDelete={() => deleteApiKeyMutation(deleteKeyId)}
          loading={isDeleting}
          confirmText="Delete"
          text="Are you sure you want to delete this API Key?"
        />
      )}
    </FormGroup>
  );
}

export default ApiKeySettings;
